// Auto Patient Report Helper
// --------------------------
// Purpose:
// Keep the patient-facing report in sync with the clinician-approved sections of the
// Clinical Assessment Report. If clinicians finish reviewing a section and all required
// core sections are fully approved (determined by requiredSectionsUtils.js), and the
// previously finalised patient report is older than those reviews (or never finalised),
// we re-render the patient report.
//
// Why this exists:
// - In report_logic_version = 2, the Patient Report reuses already reviewed/approved
//   AI outputs from CI/SSI/DD/PAD. Clinicians should not need to review a separate set.
// - We auto-regenerate the Patient Report when needed, without manual buttons, to remove friction.
//
// Design choices:
// - Read-before-write with short retry: after a section exit, Firestore server Timestamps can lag
//   for a moment. We read the latest patient document with a brief retry to avoid racing that lag.
// - Strict staleness check: we only regenerate if the latest section review time is newer than the
//   Patient Report's approved timestamp.
// - Wait-then-retry for concurrency: when another generation is already in-flight, we wait for it
//   to finish (via an onSnapshot listener) and then re-check staleness.  If the report is still
//   stale after the in-flight generation completes (because the CF didn't pick up a later section
//   edit), we trigger exactly one retry.  This guarantees correctness when CI and SSI are
//   re-approved in rapid succession without creating infinite retry loops.
// - Fire-and-forget from UI: callers don't await this; any long-running work happens in the background.
//
// TOC QbCheck extension:
// - For TOC ADHD patients, QbCheck is a passthrough form (no clinician review step).
//   The backend sets qbCheckReportApprovedAt when the form is submitted. If that
//   timestamp is newer than prReportApprovedAt, the Patient Report is stale because
//   it won't include the latest QbCheck data in the "Your QbCheck Results" section.
// - This TOC-specific rule is additive — it never fires for non-TOC patients.
// - Loop prevention: TriggerPatientReportGeneration writes prReportApprovedAt at
//   trigger time (before CFs run), which advances past qbCheckReportApprovedAt,
//   so the post-generation recheck correctly sees "not stale" → no infinite loop.

import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { TriggerPatientReportGeneration } from './TriggerPatientReportGeneration';
import { determinePatientReportType } from './reportTypeUtils';
import { checkReportLogicVersion } from './featureFlagUtils';
import { waitForProcessingComplete } from './processingStatusHelpers';
import { getRequiredCoreSections, areAllRequiredSectionsApproved } from './requiredSectionsUtils';
import { ASSESSMENT_WORKFLOW_TYPES } from '../constants/workflowConfig';

/**
 * Workflow types that use the conditional-master-template render pipeline
 * (renderPatientReportTemplateTOC CF with {{IF:}} block processing).
 * Maintained as a Set so adding a new conditional-pipeline workflow type
 * only requires one update here rather than scattered || checks.
 */
const CONDITIONAL_REPORT_TYPES = new Set([
  ASSESSMENT_WORKFLOW_TYPES.TOC_ADHD,
  ASSESSMENT_WORKFLOW_TYPES.SLAM_ADHD,
  ASSESSMENT_WORKFLOW_TYPES.WLC_ADHD,
]);

/**
 * Determine the correct render CF name based on the patient's workflow type.
 * Conditional-pipeline workflows (TOC, SLaM, WLC) use the
 * renderPatientReportTemplateTOC Cloud Function (same CF, different Master
 * Doc resolved by workflow type inside the CF). All other patients use the
 * standard render pipeline via 'renderPatientReportTemplate'.
 *
 * This value is forwarded to the generatePatientReport CF so it can chain
 * the render step server-side.  Returning an explicit CF name for every
 * workflow type (rather than undefined + downstream defaulting) ensures
 * the CF always knows which render function to invoke.
 */
export const getRenderCfName = (patientData) => {
  const awt = patientData?.workflowConfig?.assessmentWorkflowType;
  if (CONDITIONAL_REPORT_TYPES.has(awt)) {
    return 'renderPatientReportTemplateTOC';
  }
  return 'renderPatientReportTemplate';
};

// Utility: Parse Firestore Timestamp | Date | ISO string into a JS Date, or null
const toDateOrNull = (value) => {
  if (!value) return null;
  try {
    if (value.toDate && typeof value.toDate === 'function') return value.toDate();
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
};

const getManageReports = (data) => data?.dataModel?.['6_manageReports'] || {};

// Required sections logic is centralised in requiredSectionsUtils.js.
// See that module for business rules (ADHD flow, SSI-always orgs, etc.).

// Business rule: if any required section has been reviewed more recently than the last Patient Report approval,
// the Patient Report is considered stale and should be re-rendered.
const getSectionsLastReviewedMaxAt = (manageReports, requiredSections) => {
  const sections = manageReports?.sections || {};
  const lastReviewedDates = requiredSections
    .map((n) => toDateOrNull(sections?.[`${n}`]?.lastReviewedAt))
    .filter(Boolean);
  if (lastReviewedDates.length === 0) return null;
  return new Date(Math.max(...lastReviewedDates.map((d) => d.getTime())));
};

const isReportProcessing = (manageReports) => manageReports?.prReportProcessingStatus === 'processing';

const isReportStale = (manageReports, requiredSections) => {
  const approvedAt = toDateOrNull(manageReports?.prReportApprovedAt);
  // First-time bootstrap: if patient report was never approved, treat as stale
  if (!approvedAt) return true;
  const sectionsMaxReviewedAt = getSectionsLastReviewedMaxAt(manageReports, requiredSections);
  if (!sectionsMaxReviewedAt) return true; // treat as stale to allow first render
  return approvedAt.getTime() < sectionsMaxReviewedAt.getTime();
};

// TOC-specific staleness: checks whether the patient is on the TOC ADHD workflow.
// Used to conditionally include QbCheck timestamp comparison in the overall
// staleness decision — Phoenix and other workflows are completely unaffected.
const isTocWorkflow = (patientData) =>
  patientData?.workflowConfig?.assessmentWorkflowType === ASSESSMENT_WORKFLOW_TYPES.TOC_ADHD;

// For TOC patients, QbCheck is auto-approved on form submission (passthrough — no
// clinician review step like Phoenix's RD_QBCheck). If the QbCheck approval timestamp
// is newer than the last Patient Report generation anchor (prReportApprovedAt), the
// PR is stale because it won't include the latest QbCheck scores/interpretation.
const isTocQbCheckStale = (manageReports) => {
  if (!manageReports?.qbCheckReportApproved) return false;
  const qbAt = toDateOrNull(manageReports?.qbCheckReportApprovedAt);
  const prAt = toDateOrNull(manageReports?.prReportApprovedAt);
  if (!qbAt) return false;
  if (!prAt) return true; // PR never finalised — treat as stale (bootstrap)
  return qbAt.getTime() > prAt.getTime();
};

/**
 * Appointment-date staleness (SLaM / 3 Borough and any org that later enables
 * the feature). When a clinician/Support changes a per-module appointment
 * date we bump top-level appointmentDatesLastChangedAt. If that clock is
 * newer than prReportApprovedAt, the Patient Report PDF still shows the old
 * date and must be re-finalised.
 *
 * Important: this rule NEVER bootstraps a first Patient Report. It only
 * applies when a PR has already been generated or approved — date edits on
 * patients who have not yet finalised a PR are a no-op for this helper.
 */
const isAppointmentDatesStale = (manageReports, patientData) => {
  const prExists =
    manageReports?.prReportApproved === true ||
    manageReports?.prReportGenerated === true;
  if (!prExists) return false;

  const changedAt = toDateOrNull(patientData?.appointmentDatesLastChangedAt);
  const prAt = toDateOrNull(manageReports?.prReportApprovedAt);
  if (!changedAt || !prAt) return false;
  return changedAt.getTime() > prAt.getTime();
};

// Combined staleness: the Patient Report is stale if core sections are stale (any
// workflow) OR if it's a TOC patient whose QbCheck was submitted after the last PR
// generation OR if appointment dates changed after the last PR finalisation.
// This ensures TOC patients get an up-to-date PR regardless of whether
// the QbCheck was submitted before or after the core sections were approved.
const isPatientReportStale = (manageReports, requiredSections, patientData) =>
  isReportStale(manageReports, requiredSections) ||
  (isTocWorkflow(patientData) && isTocQbCheckStale(manageReports)) ||
  isAppointmentDatesStale(manageReports, patientData);

// Implementation detail: Small retry helper for eventual consistency on serverTimestamp propagation
// We keep this lightweight to avoid stressing Firestore.
const retry = async (fn, attempts = 3, delaysMs = [150, 400, 800]) => {
  let lastError;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e) {
      lastError = e;
      if (i < attempts - 1) {
        await new Promise((res) => setTimeout(res, delaysMs[i] || 500));
      }
    }
  }
  throw lastError;
};

// Internal: read the latest patient document, re-derive manageReports and
// required sections using the FRESH Firestore data, and check whether the
// patient report is stale.  Always uses the live document snapshot for
// flow-sensitive decisions (ADHD path, required sections) to avoid stale
// closure data from the React context.
// Returns { freshData, manageReports, requiredSections, stale, ... } or throws.
const checkStaleness = async (patientId, organisation) => {
  const patientRef = doc(db, 'patients', patientId);
  const snap = await getDoc(patientRef);
  if (!snap.exists()) throw new Error('patient-doc-missing');
  const freshData = snap.data();
  const manageReports = getManageReports(freshData);
  const requiredSections = getRequiredCoreSections(freshData, organisation);
  return {
    freshData,
    manageReports,
    requiredSections,
    stale: isPatientReportStale(manageReports, requiredSections, freshData),
    processing: isReportProcessing(manageReports),
    allApproved: areAllRequiredSectionsApproved(manageReports, requiredSections),
  };
};

// Public API: rerenderPatientReportIfStale
// - Safe to call any time after a clinician exits a CI/SSI/DD/PAD review screen.
// - No-ops fast if:
//   - patient is not v2,
//   - not all core sections are approved,
//   - or the Patient Report is already up to date.
// - When the report is already processing (another caller won the race), we
//   wait for that generation to finish and then re-check staleness.  If still
//   stale, we trigger exactly one retry to pick up changes that the in-flight
//   CF may have missed.
// - If needed, determines Patient Report type and triggers generation + template render.
export const rerenderPatientReportIfStale = async ({ patientId, clinicianId, patientDataModel, organisation }) => {
  if (!patientId || !clinicianId) {
    return { success: false, skipped: true, reason: 'missing-params' };
  }

  try {
    // Cache feature flag per patientId in-memory for this session
    if (!window.__reportLogicVersionCache) window.__reportLogicVersionCache = new Map();
    let version = window.__reportLogicVersionCache.get(patientId);
    if (version === undefined) {
      version = await checkReportLogicVersion(patientId);
      window.__reportLogicVersionCache.set(patientId, version);
    }
    if (version !== 2) return { success: true, skipped: true, reason: 'not-report-logic-version-2' };

    // Read the latest patient doc (with short retry) to avoid racing serverTimestamp writes
    const readLatest = async () => {
      const patientRef = doc(db, 'patients', patientId);
      const snap = await getDoc(patientRef);
      if (!snap.exists()) throw new Error('patient-doc-missing');
      return snap.data();
    };

    // Use the fresh Firestore data for all flow-sensitive decisions (ADHD path,
    // required sections, report type) — the patientDataModel from the React
    // context closure may be stale and not reflect writes from handleExitReview.
    const data = await retry(readLatest, 3);
    const manageReports = getManageReports(data);
    const requiredSections = getRequiredCoreSections(data, organisation);

    // Gate: all required core sections must be approved
    if (!areAllRequiredSectionsApproved(manageReports, requiredSections)) {
      return { success: true, skipped: true, reason: 'not-all-approved' };
    }
    // Gate: skip if not stale (checks both core section staleness and TOC QbCheck staleness)
    if (!isPatientReportStale(manageReports, requiredSections, data)) {
      return { success: true, skipped: true, reason: 'not-stale' };
    }

    // If another caller is already generating the PR, wait for it to finish
    // instead of bailing out.  After it completes we re-check staleness and
    // trigger one retry if the completed generation didn't capture our changes.
    if (isReportProcessing(manageReports)) {
      console.log('Patient report already processing — waiting for completion before re-checking staleness');
      // Explicit 12-min timeout: the combined generate+render CF has a
      // server timeout of 540s; this must exceed it so we don't give up
      // before the CF finishes and miss the post-completion retry.
      await waitForProcessingComplete(patientId, 'prReportProcessingStatus', 12 * 60 * 1000);

      const recheck = await checkStaleness(patientId, organisation);
      if (!recheck.stale || recheck.processing || !recheck.allApproved) {
        return { success: true, skipped: true, reason: 'resolved-after-wait' };
      }

      console.log('Patient report still stale after in-flight generation — triggering retry');
      const retryReportType = determinePatientReportType(recheck.freshData, organisation);
      return await TriggerPatientReportGeneration({ clinicianId, patientId, reportType: retryReportType, renderCfName: getRenderCfName(recheck.freshData) });
    }

    // Happy path: not currently processing and report is stale — trigger generation
    const reportType = determinePatientReportType(data, organisation);
    const renderCfName = getRenderCfName(data);
    const result = await TriggerPatientReportGeneration({ clinicianId, patientId, reportType, renderCfName });

    // After generation completes, re-check: another section may have been
    // approved while the CF was running.  Trigger exactly one retry if stale.
    try {
      // 12-min timeout matches the wait above — must exceed the server's
      // 540s combined generate+render timeout.
      await waitForProcessingComplete(patientId, 'prReportProcessingStatus', 12 * 60 * 1000);
      const recheck = await checkStaleness(patientId, organisation);
      if (recheck.stale && !recheck.processing && recheck.allApproved) {
        console.log('Patient report stale after generation — triggering one retry');
        const retryReportType = determinePatientReportType(recheck.freshData, organisation);
        await TriggerPatientReportGeneration({ clinicianId, patientId, reportType: retryReportType, renderCfName: getRenderCfName(recheck.freshData) });
      }
    } catch (retryErr) {
      console.error('Post-generation staleness recheck failed:', retryErr);
    }

    return result;
  } catch (error) {
    console.error('rerenderPatientReportIfStale error:', error);
    return { success: false, error: error.message || 'unknown' };
  }
};

export const __testOnly = {
  toDateOrNull,
  getSectionsLastReviewedMaxAt,
  isReportStale,
  isReportProcessing,
  isTocWorkflow,
  isTocQbCheckStale,
  isAppointmentDatesStale,
  isPatientReportStale,
  checkStaleness,
};


